import { GanttGroup, GanttHeaderTicks, GanttRoadmapRow, GanttTask, GanttTick, GanttZoomLevel } from "../models";

// ─── Constants ───────────────────────────────────────────────────────────────

/** Pixels per day for each zoom level, tuned so the visible viewport is ~700-900px wide */
/** Pixels per day per granularity, tuned so the primary unit reads at a comfortable width. */
/** Fixed pixels-per-day for the roadmap zoom levels (density-based zoom). */
export const PIXELS_PER_DAY: Record<GanttZoomLevel, number> = {
  week: 16, // ~112px per week
  month: 4.5, // ~135px per month
  quarter: 1.4, // ~127px per quarter
  year: 0.42, // ~153px per year
  // Fallback values for the window zooms; normally the density is width-derived (see WINDOW_DAYS).
  "1w": 24,
  "2w": 13,
  "1m": 4.5,
  "3m": 1.9,
  "6m": 0.95,
  "1y": 0.42,
  "5y": 0.084,
  "10y": 0.042,
  "20y": 0.021,
};

/**
 * Window scale (Model A): the label says how many days fit in the viewport. Density is derived from
 * the available width so the labelled span fills the viewport exactly. Shared by the tasks zooms
 * (1w…1y) and the project roadmap's (3m…20y).
 */
export const WINDOW_DAYS: Partial<Record<GanttZoomLevel, number>> = {
  "1w": 7,
  "2w": 14,
  "1m": 30,
  "3m": 91,
  "6m": 182,
  "1y": 365,
  "5y": 1826,
  "10y": 3653,
  "20y": 7305,
};

/**
 * Pixels-per-day for a zoom level. Window zooms are width-derived (width ÷ window days) so the
 * labelled span fills the timeline viewport; the stage roadmap's calendar units keep a fixed density.
 */
export const getPixelsPerDay = (zoom: GanttZoomLevel, availableWidth: number): number => {
  const windowDays = WINDOW_DAYS[zoom];
  if (windowDays && availableWidth > 0) return availableWidth / windowDays;
  return PIXELS_PER_DAY[zoom];
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const startOfDay = (d: Date): Date => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};

const addDays = (d: Date, days: number): Date => {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
};

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Core math ───────────────────────────────────────────────────────────────

/** Convert a date to a pixel x-offset from the timeline start */
export const dateToPixel = (date: Date, viewStart: Date, pixelsPerDay: number): number =>
  ((date.getTime() - viewStart.getTime()) / MS_PER_DAY) * pixelsPerDay;

/** Convert a pixel x-offset back to a date */
export const pixelToDate = (px: number, viewStart: Date, pixelsPerDay: number): Date =>
  new Date(viewStart.getTime() + (px / pixelsPerDay) * MS_PER_DAY);

/** First day of the month */
const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

/** First day of the year */
const startOfYear = (d: Date): Date => new Date(d.getFullYear(), 0, 1);

/** First day of the ISO week (Monday) */
const startOfWeek = (d: Date): Date => {
  const day = d.getDay(); // 0=Sun
  const diff = (day + 6) % 7; // offset to Monday
  const out = new Date(d);
  out.setDate(out.getDate() - diff);
  out.setHours(0, 0, 0, 0);
  return out;
};

// ─── View range ──────────────────────────────────────────────────────────────

/** Calculate a sensible view start/end encompassing all data + padding */
export const getRoadmapViewRange = (rows: GanttRoadmapRow[]): { start: Date; end: Date } => {
  const today = new Date();
  let min = new Date(today.getFullYear(), 0, 1); // default: start of this year
  let max = new Date(today.getFullYear() + 2, 11, 31); // default: 2 years out

  rows.forEach((row) => {
    row.phases.forEach((p) => {
      if (p.startDate < min) min = p.startDate;
      if (p.endDate > max) max = p.endDate;
    });
    row.milestones.forEach((m) => {
      if (m.date < min) min = m.date;
      if (m.date > max) max = m.date;
    });
    row.keyDates?.forEach((kd) => {
      if (kd.date < min) min = kd.date;
      if (kd.date > max) max = kd.date;
    });
  });

  const start = new Date(min);
  start.setMonth(start.getMonth() - 3);
  start.setDate(1);
  const end = new Date(max);
  end.setMonth(end.getMonth() + 3);
  end.setDate(1);
  return { start, end };
};

/** Small lead-in before today so a few recently-overdue tasks are reachable to the left. */
const TASKS_RANGE_LEAD_DAYS = 7;
const TASKS_RANGE_TRAIL_DAYS = 7;

/**
 * Forward-looking task range (Model A). The scrollable extent starts a little before today (so
 * overdue tasks are reachable) — or earlier if a task predates that — and runs to the later of the
 * last deadline or today + the current window, so the upcoming window is always fully scrollable.
 *
 * `extraTasks` covers rows that aren't held on a group: the agenda's flat list, when the caller pages
 * it itself. Without them the range would describe groups whose rows have deliberately not been loaded,
 * and every agenda bar would fall outside it.
 */
export const getTasksViewRange = (
  groups: GanttGroup[],
  windowDays: number,
  extraTasks: GanttTask[] = []
): { start: Date; end: Date } => {
  const today = startOfDay(new Date());
  const dates: Date[] = [];
  groups.forEach((g) =>
    g.tasks.forEach((t) => {
      if (t.deadlineDate) dates.push(t.deadlineDate);
    })
  );
  extraTasks.forEach((t) => {
    if (t.deadlineDate) dates.push(t.deadlineDate);
  });

  const earliest = dates.reduce((acc, d) => (d < acc ? d : acc), today);
  const latest = dates.reduce((acc, d) => (d > acc ? d : acc), today);

  const start = addDays(earliest < today ? earliest : today, -TASKS_RANGE_LEAD_DAYS);
  const windowEnd = addDays(today, windowDays);
  const end = addDays(latest > windowEnd ? latest : windowEnd, TASKS_RANGE_TRAIL_DAYS);
  return { start, end };
};

// ─── Header tick generation ───────────────────────────────────────────────────

/** Generate decade-spanning top-row ticks (2020s, 2030s …) — the coarse row of the widest roadmap
 *  windows, where a year is the finest unit that still fits a label. */
const buildDecadeTicks = (start: Date, end: Date, ppd: number): GanttTick[] => {
  const ticks: GanttTick[] = [];
  let cur = new Date(Math.floor(start.getFullYear() / 10) * 10, 0, 1);
  while (cur <= end) {
    const next = new Date(cur.getFullYear() + 10, 0, 1);
    const x = dateToPixel(cur, start, ppd);
    const xEnd = dateToPixel(next, start, ppd);
    ticks.push({ label: `${cur.getFullYear()}s`, x, width: xEnd - x });
    cur = next;
  }
  return ticks;
};

/** Generate year-spanning top-row ticks */
const buildYearTicks = (start: Date, end: Date, ppd: number): GanttTick[] => {
  const ticks: GanttTick[] = [];
  let cur = startOfYear(start);
  while (cur <= end) {
    const next = new Date(cur.getFullYear() + 1, 0, 1);
    const x = dateToPixel(cur, start, ppd);
    const xEnd = dateToPixel(next, start, ppd);
    ticks.push({ label: String(cur.getFullYear()), x, width: xEnd - x });
    cur = next;
  }
  return ticks;
};

/** Generate quarter ticks (Q1–Q4) */
const buildQuarterTicks = (start: Date, end: Date, ppd: number): GanttTick[] => {
  const ticks: GanttTick[] = [];
  let cur = new Date(start.getFullYear(), Math.floor(start.getMonth() / 3) * 3, 1);
  while (cur <= end) {
    const q = Math.floor(cur.getMonth() / 3);
    const next = new Date(cur.getFullYear(), (q + 1) * 3, 1);
    const x = dateToPixel(cur, start, ppd);
    const xEnd = dateToPixel(next, start, ppd);
    ticks.push({ label: `Q${q + 1}`, x, width: xEnd - x });
    cur = next;
  }
  return ticks;
};

/** Generate month ticks (Jan, Feb, …) */
const buildMonthTicks = (start: Date, end: Date, ppd: number): GanttTick[] => {
  const ticks: GanttTick[] = [];
  let cur = startOfMonth(start);
  while (cur <= end) {
    const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    const x = dateToPixel(cur, start, ppd);
    const xEnd = dateToPixel(next, start, ppd);
    ticks.push({ label: MONTH_SHORT[cur.getMonth()], x, width: xEnd - x });
    cur = next;
  }
  return ticks;
};

/** Generate week ticks (week commencing Mon DD) */
const buildWeekTicks = (start: Date, end: Date, ppd: number): GanttTick[] => {
  const ticks: GanttTick[] = [];
  let cur = startOfWeek(start);
  while (cur <= end) {
    const next = new Date(cur);
    next.setDate(next.getDate() + 7);
    const x = dateToPixel(cur, start, ppd);
    const xEnd = dateToPixel(next, start, ppd);
    const label = `${MONTH_SHORT[cur.getMonth()]} ${String(cur.getDate()).padStart(2, "0")}`;
    ticks.push({ label, x, width: xEnd - x });
    cur = next;
  }
  return ticks;
};

/** Generate day ticks (day-of-month number) — used by the short tasks windows for clarity. */
const buildDayTicks = (start: Date, end: Date, ppd: number): GanttTick[] => {
  const ticks: GanttTick[] = [];
  let cur = startOfDay(start);
  while (cur <= end) {
    const next = addDays(cur, 1);
    const x = dateToPixel(cur, start, ppd);
    const xEnd = dateToPixel(next, start, ppd);
    ticks.push({ label: String(cur.getDate()), x, width: xEnd - x });
    cur = next;
  }
  return ticks;
};

/**
 * Clamp ticks to the [0, maxX] timeline bounds. Calendar-unit ticks (year,
 * quarter, month, week) naturally start before viewStart or extend past
 * viewEnd; left unclamped they inflate the scroll width and leave a blank
 * strip when scrolled to the far right. Zero/negative-width ticks are dropped.
 */
const clampTicks = (ticks: GanttTick[], maxX: number): GanttTick[] =>
  ticks
    .map((tick) => {
      let { x, width } = tick;
      if (x < 0) {
        width += x;
        x = 0;
      }
      if (x + width > maxX) {
        width = maxX - x;
      }
      return { ...tick, x, width };
    })
    .filter((tick) => tick.width > 0.5);

/**
 * Build a two-row header tick set for the given zoom level and date range.
 * Top row = coarser unit, bottom row = finer unit.
 */
export const buildHeaderTicks = (
  viewStart: Date,
  viewEnd: Date,
  zoom: GanttZoomLevel,
  pixelsPerDay: number
): GanttHeaderTicks => {
  const ppd = pixelsPerDay;
  const maxX = dateToPixel(viewEnd, viewStart, ppd);
  const clamp = (rows: GanttHeaderTicks): GanttHeaderTicks => ({
    topRow: clampTicks(rows.topRow, maxX),
    bottomRow: clampTicks(rows.bottomRow, maxX),
  });

  switch (zoom) {
    // ── Windows (Model A) — day/week/month/quarter/year ticks per the design ──
    case "1w":
    case "2w":
      return clamp({
        topRow: buildMonthTicks(viewStart, viewEnd, ppd),
        bottomRow: buildDayTicks(viewStart, viewEnd, ppd),
      });
    case "1m":
    case "3m":
      return clamp({
        topRow: buildMonthTicks(viewStart, viewEnd, ppd),
        bottomRow: buildWeekTicks(viewStart, viewEnd, ppd),
      });
    case "6m":
    case "1y":
      return clamp({
        topRow: buildYearTicks(viewStart, viewEnd, ppd),
        bottomRow: buildMonthTicks(viewStart, viewEnd, ppd),
      });
    case "5y":
      return clamp({
        topRow: buildYearTicks(viewStart, viewEnd, ppd),
        bottomRow: buildQuarterTicks(viewStart, viewEnd, ppd),
      });
    // A month label has no room at these spans, so the finest readable unit is the year.
    case "10y":
    case "20y":
      return clamp({
        topRow: buildDecadeTicks(viewStart, viewEnd, ppd),
        bottomRow: buildYearTicks(viewStart, viewEnd, ppd),
      });
    // ── Stage roadmap zooms — fixed-density calendar units ──
    case "week":
      return clamp({
        topRow: buildMonthTicks(viewStart, viewEnd, ppd),
        bottomRow: buildWeekTicks(viewStart, viewEnd, ppd),
      });
    case "month":
      return clamp({
        topRow: buildYearTicks(viewStart, viewEnd, ppd),
        bottomRow: buildMonthTicks(viewStart, viewEnd, ppd),
      });
    case "quarter":
      return clamp({
        topRow: buildYearTicks(viewStart, viewEnd, ppd),
        bottomRow: buildQuarterTicks(viewStart, viewEnd, ppd),
      });
    case "year":
      return clamp({
        topRow: buildYearTicks(viewStart, viewEnd, ppd),
        bottomRow: buildYearTicks(viewStart, viewEnd, ppd).map((t) => ({ ...t, label: "" })),
      });
    default:
      return clamp({
        topRow: buildYearTicks(viewStart, viewEnd, ppd),
        bottomRow: buildQuarterTicks(viewStart, viewEnd, ppd),
      });
  }
};

/** Total pixel width of the timeline at the given density. */
export const getTimelineWidth = (viewStart: Date, viewEnd: Date, pixelsPerDay: number): number =>
  Math.ceil(((viewEnd.getTime() - viewStart.getTime()) / MS_PER_DAY) * pixelsPerDay);

/** Pixel x position of today within the timeline. */
export const getTodayX = (viewStart: Date, pixelsPerDay: number): number =>
  dateToPixel(new Date(), viewStart, pixelsPerDay);
